import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import { specials } from "@/mocks/specials";
import { categoryConfigs } from "@/mocks/categories";
import type { Movie, NewsItem, SpecialItem, CategoryConfig } from "@/types/movie";

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return movies.find((m) => m.id === id);
}

export function getNewsById(id: string | undefined): NewsItem | undefined {
  if (!id) return undefined;
  return newsList.find((n) => n.id === id);
}

export function getSpecialById(id: string | undefined): SpecialItem | undefined {
  if (!id) return undefined;
  return specials.find((s) => s.id === id);
}

export function getCategoryBySlug(slug: string | undefined): CategoryConfig {
  const found = categoryConfigs.find((c) => c.slug === slug);
  return found ?? categoryConfigs[0];
}

export function getMoviesByIds(ids: string[]): Movie[] {
  return ids
    .map((id) => getMovieById(id))
    .filter((m): m is Movie => Boolean(m));
}

export function getFeaturedMovies(): Movie[] {
  return movies.filter((m) => m.featured);
}

export function getHotMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.heat - a.heat).slice(0, limit);
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...movies]
    .sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1))
    .slice(0, limit);
}

export function getClassicMovies(): Movie[] {
  return [...movies].sort((a, b) => b.rating - a.rating).filter((m) => m.rating >= 8.5);
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  const sameDirectorOrGenre = movies.filter(
    (m) =>
      m.id !== movie.id &&
      (m.director === movie.director ||
        m.genres.some((g) => movie.genres.includes(g)) ||
        m.region === movie.region)
  );
  return sameDirectorOrGenre.slice(0, limit);
}

export function getLatestNews(limit = 6): NewsItem[] {
  return [...newsList]
    .sort((a, b) => (a.publishTime < b.publishTime ? 1 : -1))
    .slice(0, limit);
}

export function getRelatedNews(current: NewsItem, limit = 3): NewsItem[] {
  const sameCategory = newsList.filter(
    (n) => n.id !== current.id && n.category === current.category
  );
  const others = newsList.filter(
    (n) => n.id !== current.id && n.category !== current.category
  );
  return [...sameCategory, ...others].slice(0, limit);
}

export function getOtherSpecials(currentId: string, limit = 3): SpecialItem[] {
  return specials.filter((s) => s.id !== currentId).slice(0, limit);
}

export interface SearchResult {
  movies: Movie[];
  news: NewsItem[];
}

export function searchContent(keyword: string): SearchResult {
  const q = keyword.trim().toLowerCase();
  if (!q) return { movies: [], news: [] };
  return {
    movies: movies.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        m.director.toLowerCase().includes(q) ||
        m.genres.some((g) => g.toLowerCase().includes(q)) ||
        m.actors.some((a) => a.toLowerCase().includes(q))
    ),
    news: newsList.filter(
      (n) =>
        n.title.toLowerCase().includes(q) ||
        n.summary.toLowerCase().includes(q)
    ),
  };
}